import React, { useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import {
    FaBold,
    FaItalic,
    FaUnderline,
    FaListUl,
    FaListOl,
    FaQuoteRight,
    FaUndo,
    FaRedo,
    FaAlignLeft,
    FaAlignCenter,
    FaAlignRight,
    FaImage,
    FaLink,
    FaHighlighter,
    FaEraser
} from 'react-icons/fa';
import './RichTextEditor.css';

const RichTextEditor = ({ initialContent = '', onChange, placeholder = 'Write something...' }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Image.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: {
                    class: 'editor-image',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
                alignments: ['left', 'center', 'right'],
            }),
            Underline,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'editor-link',
                },
            }),
            Highlight,
        ],
        content: initialContent,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            if (onChange) onChange(html);
        },
        editorProps: {
            attributes: {
                class: 'ProseMirror-editor',
                placeholder: placeholder
            }
        }
    });

    const handleImageUpload = () => {
        fileInputRef.current?.click();
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploading(true);
            const reader = new FileReader();
            reader.onload = (e) => {
                const imageUrl = e.target.result;
                editor?.chain().focus().setImage({ src: imageUrl }).run();
                setUploading(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const addLink = () => {
        const url = window.prompt('Enter URL:', 'https://');
        if (url && editor) {
            editor.chain().focus().setLink({ href: url }).run();
        }
    };

    if (!editor) {
        return null;
    }

    const MenuButton = ({ onClick, isActive, icon, title }) => (
        <button
            onClick={onClick}
            className={`menu-button ${isActive ? 'active' : ''}`}
            title={title}
            type="button"
        >
            {icon}
        </button>
    );

    const Divider = () => <div className="menu-divider"></div>;

    return (
        <div className="rich-text-editor">
            <div className="toolbar">
                {/* Text Formatting */}
                <MenuButton
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive('bold')}
                    icon={<FaBold />}
                    title="Bold (Ctrl+B)"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive('italic')}
                    icon={<FaItalic />}
                    title="Italic (Ctrl+I)"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive('underline')}
                    icon={<FaUnderline />}
                    title="Underline (Ctrl+U)"
                />

                <Divider />

                {/* Headings */}
                <select
                    onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'paragraph') {
                            editor.chain().focus().setParagraph().run();
                        } else {
                            editor.chain().focus().toggleHeading({ level: parseInt(value) }).run();
                        }
                    }}
                    className="heading-select"
                    value={editor.isActive('heading') ? editor.getAttributes('heading').level : 'paragraph'}
                >
                    <option value="paragraph">Normal</option>
                    <option value="1">Heading 1</option>
                    <option value="2">Heading 2</option>
                    <option value="3">Heading 3</option>
                    <option value="4">Heading 4</option>
                </select>

                <Divider />

                {/* Alignment */}
                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    isActive={editor.isActive({ textAlign: 'left' })}
                    icon={<FaAlignLeft />}
                    title="Align Left"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    isActive={editor.isActive({ textAlign: 'center' })}
                    icon={<FaAlignCenter />}
                    title="Align Center"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    isActive={editor.isActive({ textAlign: 'right' })}
                    icon={<FaAlignRight />}
                    title="Align Right"
                />

                <Divider />

                {/* Lists */}
                <MenuButton
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive('bulletList')}
                    icon={<FaListUl />}
                    title="Bullet List"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive('orderedList')}
                    icon={<FaListOl />}
                    title="Numbered List"
                />

                <Divider />

                {/* Media & Content */}
                <MenuButton
                    onClick={handleImageUpload}
                    icon={<FaImage />}
                    title="Insert Image"
                />
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept="image/*"
                    onChange={handleImageChange}
                />
                {uploading && <span className="upload-status">Uploading...</span>}

                <MenuButton
                    onClick={addLink}
                    isActive={editor.isActive('link')}
                    icon={<FaLink />}
                    title="Add Link"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive('blockquote')}
                    icon={<FaQuoteRight />}
                    title="Quote"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().toggleHighlight().run()}
                    isActive={editor.isActive('highlight')}
                    icon={<FaHighlighter />}
                    title="Highlight Text"
                />

                <Divider />

                {/* Utilities */}
                <MenuButton
                    onClick={() => editor.chain().focus().undo().run()}
                    icon={<FaUndo />}
                    title="Undo (Ctrl+Z)"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().redo().run()}
                    icon={<FaRedo />}
                    title="Redo (Ctrl+Y)"
                />
                <MenuButton
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                    icon={<FaEraser />}
                    title="Clear Formatting"
                />
            </div>
            <EditorContent editor={editor} />
        </div>
    );
};

export default RichTextEditor;